import $ from "jquery";
import "jquery.cookie";
import Handlebars from "handlebars";

const openStateEnum = {
  CLOSED: "closed",
  WILL_OPEN: "will open",
  OPEN: "open",
};

export function getMyLocation(callback) {
  navigator.geolocation.getCurrentPosition(function (position) {
    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;
    callback(`${latitude},${longitude}`);
  });
}

/**
 *   Returns standard time string
 *   @param militaryTime eg: "2000", "730"
 */
function convertMilitaryToStandard(militaryTime) {
  const military = Number(militaryTime);

  // Bad format
  if (military < 0 || military > 2400) {
    return 0;
  }

  const milHours = militaryTime / 100;
  const hours = Math.floor(milHours >= 13 ? milHours - 12 : milHours);
  const mins = militaryTime.slice(-2);

  const suffix = milHours > 12 ? "PM" : "AM";

  return `${hours}:${mins} ${suffix}`;
}

/**
 *   Returns {state, time}
 *   @param day {open: "", close: ""}
 *   @param currentHour Current hour of the day, 0-2400
 */
function isStoreOpen(day, currentHour) {
  // Not open at all today
  if (day.open == "0" && day.close == "0")
    return { state: openStateEnum.CLOSED, time: 0 };

  if (currentHour < Number(day.close)) {
    if (currentHour >= Number(day.open)) {
      // Store is open currently, and will close at...
      return {
        state: openStateEnum.OPEN,
        time: convertMilitaryToStandard(day.close),
      };
    } else {
      // Store is currently closed, but will open today at...
      return {
        state: openStateEnum.WILL_OPEN,
        time: convertMilitaryToStandard(day.open),
      };
    }
  }

  return { state: openStateEnum.CLOSED, time: 0 }; // After closing time
}

function getDayStringFromIndex(dayIndex) {
  const dayStrings = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  return dayStrings[dayIndex];
}

/**
 * Returns array of {dayString, openTimeString, closeTimeString, isOpen}
 * @param {*} days Array of {openTimeString, closeTimeString}, in military time string format, eg: "2000", "730"
 */
function getTimeStringsFromDays(days) {
  return days.map((day, index) => {
    return {
      dayString: getDayStringFromIndex(index),
      openTimeString: convertMilitaryToStandard(day.open),
      closeTimeString: convertMilitaryToStandard(day.close),
      isOpen: day.open != 0 && day.close != 0,
    };
  });
}

/**
 * Returns array of {dayString, openTimeString, closeTimeString, isOpen} with today at the beginning of the list
 * @param {*} days Array of {openTimeString, closeTimeString}, in military time string format, eg: "2000", "730"
 */
function orderDays(days) {
  const now = new Date();
  const day = now.getDay();
  if (day !== 0) {
    const firstHalf = days.slice(day);
    const lastHalf = days.slice(0, day);
    return firstHalf.concat(lastHalf);
  }
  return days;
}

/**
 * Returns {message, isOpen, cssClass, todayOpenTimeString, todayCloseTimeString, allTimes}
 * @param {*} days Array of {open, close}, in military time string format, eg: "2000", "730"
 */
function getOpenInfo(days, openUntilClass, opensAtClass, closedClass) {
  const now = new Date();
  const hour = now.getHours() * 100;
  const day = now.getDay();
  const isOpen = isStoreOpen(days[day], hour);

  const todayOpenString = convertMilitaryToStandard(days[day].open);
  const todayCloseString = convertMilitaryToStandard(days[day].close);

  const allTimes = orderDays(getTimeStringsFromDays(days));

  switch (isOpen.state) {
    case openStateEnum.OPEN:
      return {
        message: `Open Now: ${todayOpenString} - ${todayCloseString}`,
        isOpen: true,
        cssClass: openUntilClass,
        todayOpenTimeString: todayOpenString,
        todayCloseTimeString: todayCloseString,
        allTimes: allTimes,
      };
    case openStateEnum.WILL_OPEN:
      return {
        message: `Opens at ${isOpen.time}`,
        isOpen: false,
        cssClass: opensAtClass,
        todayOpenTimeString: todayOpenString,
        todayCloseTimeString: todayCloseString,
        allTimes: allTimes,
      };
    case openStateEnum.CLOSED:
      return {
        message: "Closed",
        isOpen: false,
        cssClass: closedClass,
        allTimes: allTimes,
      };
  }

  return {
    message: "Closed",
    isOpen: false,
    cssClass: closedClass,
    allTimes: allTimes,
  };
}

// This is deprecated in favor of using a handlebars registerHelper
export function formatAndCreateHourInfo(
  days,
  openUntilStyle,
  opensAtStyle,
  closedStyle,
  openUntilId,
  todayHoursId,
  allHoursId
) {
  const openInfo = getOpenInfo(days, openUntilStyle, opensAtStyle, closedStyle);
  $(openUntilId).html(openInfo.message);
  $(openUntilId).addClass(openInfo.cssClass);

  // Don't display today's hours if store is closed all day
  if (openInfo.isOpen) {
    const todayHoursString = `${openInfo.todayOpenTimeString} - ${openInfo.todayCloseTimeString}`;
    $(todayHoursId).html(todayHoursString);
  }

  // Fill in all store hours
  const allHoursList = $(allHoursId);
  openInfo.allTimes.forEach((day) => {
    const dayHoursString = day.isOpen
      ? `${day.openTimeString} - ${day.closeTimeString}`
      : `Closed`;
    allHoursList.append(`<li>${day.dayString}: ${dayHoursString}</li>`);
  });
}

function createHoursArray(location) {
  return [
    { open: location.open_sunday, close: location.close_sunday },
    { open: location.open_monday, close: location.close_monday },
    { open: location.open_tuesday, close: location.close_tuesday },
    { open: location.open_wednesday, close: location.close_wednesday },
    { open: location.open_thursday, close: location.close_thursday },
    { open: location.open_friday, close: location.close_friday },
    { open: location.open_saturday, close: location.close_saturday },
  ];
}

/**
 * Returns an html element with text describing the open state of the store
 * @param {*} location store location object
 */
export function openUntil(location) {
  const openInfo = getOpenInfo(
    createHoursArray(location),
    "open-until",
    "opens-at",
    "closed"
  );
  //const htmlString = `<div class="${openInfo.cssClass}">${openInfo.message}</div>`
  const htmlString = `${openInfo.message}`;
  return htmlString;
}

/**
 * Returns an html element with text describing today's hours
 * @param {*} location store location object
 */
export function todayHours(location) {
  const openInfo = getOpenInfo(
    createHoursArray(location),
    "open-until",
    "opens-at",
    "closed"
  );
  const now = new Date();
  const day = now.getDay();
  // Don't display today's hours if store is closed all day
  if (openInfo.allTimes[day].isOpen) {
    const todayHoursString = `${openInfo.allTimes[day].openTimeString} - ${openInfo.allTimes[day].closeTimeString}`;
    return todayHoursString;
  }
  return "Closed all day";
}

/**
 * Returns an html list element with all store hours
 * @param {*} location store location object
 * @param {*} ulStyle css style to apply to created list
 */
export function allHours(location, ulStyle) {
  const openInfo = getOpenInfo(
    createHoursArray(location),
    "open-until",
    "opens-at",
    "closed"
  );
  let htmlString = `<ul class="${ulStyle}">`;
  openInfo.allTimes.forEach((day, index) => {
    const dayHoursString = day.isOpen
      ? `${day.openTimeString} - ${day.closeTimeString}`
      : `Closed`;
    const liTodayStyle = openInfo.cssClass;
    const liDefaultStyle = "";
    const liStyle = index === 0 ? liTodayStyle : liDefaultStyle;
    htmlString = htmlString.concat(
      `<li class="${liStyle}">${day.dayString}: ${dayHoursString}</li>`
    );
  });
  htmlString = htmlString.concat(`</ul>`);
  return htmlString;
}

// Handlebars helpers (for the store locator)

/**
 * Returns an html element with text describing the open state of the store
 * @param {*} location store location object
 */
Handlebars.registerHelper("openUntil", function (location) {
  return new Handlebars.SafeString(openUntil(location));
});

/**
 * Returns an html element with text describing today's hours
 * @param {*} location store location object
 */
Handlebars.registerHelper("todayHours", function (location) {
  const openInfo = getOpenInfo(
    createHoursArray(location),
    "open-until",
    "opens-at",
    "closed"
  );
  const now = new Date();
  const day = now.getDay();
  // Don't display today's hours if store is closed all day
  if (openInfo.allTimes[day].isOpen) {
    const todayHoursString = `${openInfo.allTimes[day].openTimeString} - ${openInfo.allTimes[day].closeTimeString}`;
    return new Handlebars.SafeString(todayHoursString);
  }
  return new Handlebars.SafeString("Closed all day");
});

/**
 * Returns an html list element with all store hours
 * This is legacy code; was only used on store details
 * page, which now no longer uses handlebars
 * @param {*} location store location object
 */
Handlebars.registerHelper("allHours", function (location) {
  const openInfo = getOpenInfo(
    createHoursArray(location),
    "open-until",
    "opens-at",
    "closed"
  );
  let htmlString = `<ul>`;
  openInfo.allTimes.forEach((day) => {
    const dayHoursString = day.isOpen
      ? `${day.openTimeString} - ${day.closeTimeString}`
      : `Closed`;
    htmlString = htmlString.concat(
      `<li>${day.dayString}: ${dayHoursString}</li>`
    );
  });
  htmlString = htmlString.concat(`</ul>`);
  return new Handlebars.SafeString(htmlString);
});

Handlebars.registerHelper("phoneNumber", function (text) {
  const cleaned = ("" + text).replace(/\D/g, "");
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const intlCode = match[1] ? "+1 " : "";
    const result = Handlebars.escapeExpression(
      [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("")
    );
    return new Handlebars.SafeString(result);
  }
  return new Handlebars.SafeString(text);
});

Handlebars.registerHelper("allinfotest", function (text) {
  return new Handlebars.SafeString(text);
});

export function setHomeStore(store, origin) {
  $.cookie("homeAceStore", store, { path: "/", secure: false });
}

export function getHomeStore() {
  const storeAddress = $.cookie("homeAceStore");
  return { store: storeAddress };
}

export function isMyHomeStore(store) {
  const currentStore = $.cookie("homeAceStore");
  return store === currentStore;
}

export function setMyHomeStore(
  buttonID,
  buttonClassPrefix,
  address,
  selectedStyle,
  unselectedStyle,
  selectedText = "My store",
  unselectedText = "Make it my store"
) {
  // Deselect all first
  const className = selectedStyle.replace(/ /g, ".");
  $("." + className).attr("class", unselectedStyle);
  $(`[id^="${buttonClassPrefix}"]`).text(unselectedText);

  if (isMyHomeStore(address) !== true) {
    setHomeStore(address);
    $("#" + buttonID).attr("class", selectedStyle);
    $("#" + buttonID).text(selectedText);
  } else {
    setHomeStore("", "");
  }
}

export function setMyHomeStoreButtonStyle(
  buttonID,
  storeAddress,
  selectedStyle,
  unselectedStyle,
  selectedText = "My store",
  unselectedText = "Make it my store"
) {
  const button = $("#" + buttonID);
  button.attr(
    "class",
    isMyHomeStore(storeAddress) ? selectedStyle : unselectedStyle
  );
  button.text(isMyHomeStore(storeAddress) ? selectedText : unselectedText);
}

export function selectDistance(value, id, form) {
  $("#" + id).val(value);
  $("#" + form).submit();
}

export function toggleFilters(normalForm, filtersForm) {
  const nf = $("#" + normalForm);
  const ff = $("#" + filtersForm);
  if (nf.is(":hidden")) {
    nf.show();
    ff.hide();
  } else {
    nf.hide();
    ff.show();
  }
}

export function loadMoreStores(){
  // $(".store-listing").hide();
  console.log("hidden length:", $(".store-listing:hidden").length)
  $(".store-listing").slice(0, 5).show(); // select the first 5
  if($(".store-listing").length <= 5){
    $(".load-more-stores-btn").hide();
  }else{
    $(".load-more-stores-btn").css({display: 'block'});
  }
  $(".load-more-stores-btn").click(function(e){ // click event for load more
      e.preventDefault();
      console.log("hidden length (before slice):", $(".store-listing:hidden").length)
      $(".store-listing:hidden").slice(0, 5).show(); // select next 5 hidden divs and show them
      console.log("hidden length (after slice):", $(".store-listing:hidden").length)
      if($(".store-listing:hidden").length == 0){ // check if any hidden divs still exist
        $(".load-more-stores-btn").hide();
      }else{
        $(".load-more-stores-btn").css({display: 'block'});
      }
  });
}
